import axios from 'axios'
import React, {useEffect, useState} from 'react'
import {useHistory, useParams} from 'react-router-dom'
import {NEW_PHYSICAL_THERAPIST_CONSTANT} from '../../../Notes/Physical_Therapist_Notes/PHYSICAL_THERAPIST_CONSTANT'
import DynamicForm from '../../../../reusable-components/forms/DynamicForm'
import {toast} from 'react-toastify'
import dayjs from 'dayjs'
import {LocalizationProvider} from '@mui/x-date-pickers'
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs'
import {Typography} from '@mui/material'
import {MoonLoader} from 'react-spinners'
import FormWrapper from '../../../../reusable-components/Wrapper/Wrapper'
import {RootState} from '../../../../../setup'
import {useSelector} from 'react-redux'
import FrequencyDateRangePicker from '../../../../reusable-components/NoteType/NoteType'

const selectTimeZone = (state: RootState) => state.timeZone.timeZone

const DeletedPhysicalTherapistNoteForm = () => {
  const params: {id: string} = useParams()
  const history = useHistory()
  const timeZone = useSelector(selectTimeZone)
  const [isFormLoading, setIsFormLoading] = useState(true)
  const [uiElements, setUiElements] = useState([])
  const [formData, setFormData] = useState({})
  const [noteData, setNoteData] = useState({
    noteType: '',
    startDate: null,
    endDate: null,
    timeIn: '',
    timeOut: '',
  })
  const [clientid, setClientId] = useState<string | null>(null)

  const buttons = [{label: 'Cancel', variant: 'outlined', type: 'button'}]

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const res = await axios.get(`/physical-notes/${params.id}?isDeleted=true`)
      const data = res.data.data

      const prevFormData = {...formData}
      const formattedStartDate = dayjs.utc(data?.startDate).tz(timeZone, true)
      const formattedEndDate = dayjs.utc(data?.endDate).tz(timeZone, true)

      const tempData = {
        ...data,
        noteType: data?.noteType,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        timeIn: data?.timeIn,
        timeOut: data?.timeOut,
        emergencyContactName: data?.emergencyContact?.[0]?.contactName,
        relationship: data?.emergencyContact?.[0]?.relationship,
        phoneNumber: data?.emergencyContact?.[0]?.contactNumber,
        emergencyContactName1: data?.emergencyContact?.[1]?.contactName,
        relationship1: data?.emergencyContact?.[1]?.relationship,
        phoneNumber1: data?.emergencyContact?.[1]?.contactNumber,
        referringProviderName: data?.referringProvider?.name,
        referringProviderPhoneNumber: data?.referringProvider?.phoneNumber,
        primaryCareProviderName: data?.primaryCareProvider?.name,
        primaryCareProviderPhoneNumber: data?.primaryCareProvider?.phoneNumber,
        insurance: data?.insuranceProvider?.name,
        insurancePolicyNumber: data?.insuranceProvider?.policyNumber,
        insuranceProvider: data?.insuranceProvider?.policyHolderName,
        desease: data?.medicalIssues,
        concernsOrWorry: data?.concernsRegardingTherapy,
        referringPhysicianName: data?.referringPhysician?.name,
        referringPhysicianPhoneNumber: data?.referringPhysician?.phone,
        planOfCareDate: data?.referringPhysician?.planOfCareDate ? dayjs.utc(data.referringPhysician.planOfCareDate).tz(timeZone, true) : null,
        dateOfOnset: data?.referringPhysician?.dateOfOnset ? dayjs.utc(data.referringPhysician.dateOfOnset).tz(timeZone, true) : null,
        dateOfEvaluation: data?.referringPhysician?.dateOfEvaluation ? dayjs.utc(data.referringPhysician.dateOfEvaluation).tz(timeZone, true) : null,
        summaryOfEvaluationFindings: data?.summaryOfEvaluation,
        treatmentDiagnosis: data?.diagnosis?.treatmentDiagnosis,
        medicalDiagnosis: data?.diagnosis?.medicalDiagnosis,
        shortTermGoals: data?.treatmentGoals?.shortTerm,
        longTermGoals: data?.treatmentGoals?.longTerm,
        planOfCareTreatmentPlan: data?.treatmentPlan,
        insuranceIdNumber: data?.additionalNotes?.insuranceIdNumber,
        dateOfInjury: data?.additionalNotes?.dateOfInjury ? dayjs.utc(data.additionalNotes.dateOfInjury).tz(timeZone, true) : null,
        modalityType: data?.additionalNotes?.modalityType,
        nextSessionDate: data?.nextSession ? dayjs.utc(data.nextSession).tz(timeZone, true) : null,
        therapyDate: data?.therapyDate ? dayjs.utc(data.therapyDate).tz(timeZone, true) : null,
        currentMedications: data?.currentMedications,
        narrativeInformation: data?.narrativeInformation,
        subjective: data?.subjective,
        objective: data?.objective,
        assessment: data?.assessment,
        patientGoals: data?.patientGoals,
        assessmentRecords: data?.painLocation || [],
      }

      setFormData(tempData)
      setNoteData({
        noteType: data?.noteType,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        timeIn: data?.timeIn,
        timeOut: data?.timeOut,
      })

      setUiElements(
        NEW_PHYSICAL_THERAPIST_CONSTANT.map((item) => ({
          ...item,
          disabled: true,
          ...(item.type === 'PhysicalTherapistDialog' && { isDisabled: true }),
        }))
      )
      setClientId(data.clientId)
    } catch (error) {
      if (error.response) {
        toast.error(`Error: ${error.response.data.message || 'Something went wrong'}`)
      } else if (error.request) {
        toast.error('No response received from the server.')
      } else {
        toast.error('Error: Something went wrong')
      }
      console.error('Error fetching data:', error)
    } finally {
      setIsFormLoading(false)
    }
  }

  if (isFormLoading) {
    return (
      <div className='overlay d-flex justify-content-center' style={{marginTop: '100px'}}>
        <MoonLoader color='#9db2fc' size={60} loading={isFormLoading} />
      </div>
    )
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <FormWrapper
        title='Deleted Physical Therapist Entry'
        content={
          <>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 'bold',
                marginBottom: '0.5rem',
                marginLeft: '1rem',
              }}
              color='primary'
            >
              Note Type
            </Typography>
            <FrequencyDateRangePicker
              isEntry={true}
              isTemplate={false}
              isFullForm={false}
              isEdit={true}
              formData={noteData}
              setFormData={setNoteData}
              isDeleted={true}
            />
            <DynamicForm
              fields={uiElements}
              data={formData}
              onSubmit={() => {}}
              buttons={buttons}
              disabled={true}
            />
          </>
        }
      />
    </LocalizationProvider>
  )
}

export default DeletedPhysicalTherapistNoteForm
